import { isWidgetAuthExpiredError, webchatApi } from './api';
import { createEventDispatcher, readStream, type StreamCallbacks } from './stream';
import type { ChatEventDispatcher, TurnPollTarget } from '../types';

/**
 * The server sends a heartbeat every 5s on a live /webchat/chat stream, so this much silence means
 * the response is being held back somewhere between server and browser - typically a corporate
 * proxy doing SSL inspection / AV scanning that only releases a response once it completes.
 * Such a proxy does pass short responses through, so we switch to polling GET chat/events.
 */
export const STREAM_SILENCE_MS = 15_000;
/**
 * Gap before the next poll after one that returned events. While the model is writing there is
 * almost always a new text_delta, so without a gap every poll returns immediately with a token
 * or two - a request per few tokens, which trips the global per-IP throttler (10/s, 100/min)
 * that a whole customer behind one proxy egress IP shares. Empty polls need no gap: the server
 * already long-polled for up to ~20s.
 */
export const POLL_BATCH_INTERVAL_MS = 2_000;
const POLL_RETRY_DELAY_MS = 2_000;
const POLL_THROTTLED_DEFAULT_DELAY_MS = 5_000;
/** A 404 right after switching can mean the run isn't registered yet (request still in flight). */
const MAX_POLL_NOT_FOUND = 3;
const MAX_POLL_FAILURES = 5;

const delay = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
  });

/**
 * Sends the chat request and waits for its response headers, but gives up waiting after
 * STREAM_SILENCE_MS. Returns null in that case: the caller should read the run by polling.
 * The request is left running (aborting it could cut it off before it reached the server);
 * if its response shows up later, its body is discarded.
 */
export async function awaitChatResponse(request: Promise<Response>): Promise<Response | null> {
  let gaveUp = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const silence = new Promise<null>((resolve) => {
    timer = setTimeout(() => { gaveUp = true; resolve(null); }, STREAM_SILENCE_MS);
  });

  request
    .then((res) => { if (gaveUp) res.body?.cancel().catch(() => { /* ignore */ }); })
    .catch(() => { /* surfaced by the race below, or irrelevant once polling */ });

  try {
    return await Promise.race([request, silence]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Reads one chat round trip's events into `callbacks`: from the SSE `response` while it keeps
 * delivering, and by polling the server's recorded copy of the run when it goes silent (or when
 * `response` is null - its headers never arrived). Without a poll target this is plain readStream.
 */
export function readChatResponse(
  response: Response | null,
  callbacks: StreamCallbacks,
  pollTarget?: TurnPollTarget,
): Promise<void> {
  if (!pollTarget) {
    if (response) return readStream(response, callbacks);
    callbacks.onError(new Error('No chat response to read'));
    return Promise.resolve();
  }

  const dispatcher = createEventDispatcher(callbacks);
  if (!response) return pollTurnEvents(dispatcher, callbacks, pollTarget);

  const stopSse = new AbortController();
  let lastActivity = Date.now();
  let polling: Promise<void> | null = null;

  const watchdog = setInterval(() => {
    if (dispatcher.finished || pollTarget.signal.aborted) {
      clearInterval(watchdog);
      return;
    }
    if (Date.now() - lastActivity < STREAM_SILENCE_MS) return;
    clearInterval(watchdog);
    console.warn('[mcp-chat-widget] Chat stream silent - switching to polling (buffering proxy?)');
    stopSse.abort();
    polling = pollTurnEvents(dispatcher, callbacks, pollTarget);
  }, 1_000);

  // Settles once the SSE reader is done - and, if it was handed over, once polling is done too.
  return readStream(response, callbacks, {
    dispatcher,
    stopSignal: stopSse.signal,
    onActivity: () => { lastActivity = Date.now(); },
  }).then(() => {
    clearInterval(watchdog);
    // stopSse is only aborted in the same tick that starts polling, so `polling` is set by now.
    return polling ?? undefined;
  });
}

async function pollTurnEvents(
  dispatcher: ChatEventDispatcher,
  callbacks: StreamCallbacks,
  { turnId, roundTrip, signal }: TurnPollTarget,
): Promise<void> {
  let notFound = 0;
  let failures = 0;

  while (!dispatcher.finished && !signal.aborted) {
    try {
      const result = await webchatApi.pollTurnEvents(turnId, roundTrip, dispatcher.lastSeq, signal);
      notFound = 0;
      failures = 0;
      // The server answered - the run is alive. Same liveness signal as an SSE heartbeat
      // (resets the turn's inactivity timeout via onEvent).
      dispatcher.dispatch({ type: 'heartbeat' });
      for (const event of result.events) dispatcher.dispatch(event);
      if (result.done) dispatcher.finishWithoutDoneEvent();
      else if (result.events.length > 0) await delay(POLL_BATCH_INTERVAL_MS, signal);
    } catch (err) {
      if (signal.aborted) return;
      if (isWidgetAuthExpiredError(err)) {
        callbacks.onError(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      const { status, retryAfterMs } = (err ?? {}) as { status?: number; retryAfterMs?: number };
      if (status === 429) {
        // Throttled is not a failure of the run - just wait it out.
        await delay(retryAfterMs ?? POLL_THROTTLED_DEFAULT_DELAY_MS, signal);
        continue;
      }
      const exhausted = status === 404 ? ++notFound >= MAX_POLL_NOT_FOUND : ++failures >= MAX_POLL_FAILURES;
      if (exhausted) {
        callbacks.onError(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      await delay(POLL_RETRY_DELAY_MS, signal);
    }
  }
}
